<!-- Full-screen loading overlay -->
<div
  *ngIf="loading"
  class="fullscreen-loading-overlay"
  role="status"
  aria-live="polite"
  aria-label="Sending to Rev.io"
>
  <div class="flex flex-column align-items-center gap-3">
    <p-progressSpinner
      [style]="{ width: '80px', height: '80px' }"
      strokeWidth="4"
      animationDuration="1s"
    ></p-progressSpinner>
    <span class="text-white text-xl font-semibold">Sending to Rev.io...</span>
  </div>
</div>

<div class="flex flex-column gap-3">
  <div class="flex align-items-center justify-content-end mb-3">
    <p-button
      label="Export Summary"
      icon="pi pi-download"
      (onClick)="exportSummary()"
      [disabled]="!onBoardingRecord"
    ></p-button>
  </div>
  <p-card header="Customer Information">
    <div class="grid">
      <div class="col-12 md:col-6">
        <strong>Name:</strong> {{ onBoardingRecord?.customer?.name }}
      </div>
      <div class="col-12 md:col-6">
        <strong>Address:</strong>
        {{ onBoardingRecord?.customer?.addressLine1 }},
        {{ onBoardingRecord?.customer?.city }},
        {{ onBoardingRecord?.customer?.state }}
        {{ onBoardingRecord?.customer?.zipCode }}
      </div>
    </div>
  </p-card>

  <p-card header="Locations">
    <p-table
      [value]="onBoardingRecord?.locations || []"
      [paginator]="true"
      [rows]="5"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Company Name</th>
          <th>Address</th>
          <th>City</th>
          <th>State</th>
          <th>Zip Code</th>
          <th>Status</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-location>
        <tr>
          <td>{{ location.companyName }}</td>
          <td>{{ location.addressLine1 }}</td>
          <td>{{ location.city }}</td>
          <td>{{ location.state }}</td>
          <td>{{ location.zipCode }}</td>
          <td>
            <ng-container *ngIf="onBoardingRecord?.parentCustomerId; else noParent">
              <span
                *ngIf="sentLocationNames.has(location.companyName); else pendingLocation"
                class="text-green-600"
                pTooltip="Created in Rev.io"
                tooltipPosition="top"
              >
                <i class="pi pi-check-circle"></i> Sent
              </span>
              <ng-template #pendingLocation>
                <span
                  class="text-orange-500"
                  pTooltip="Not yet sent to Rev.io"
                  tooltipPosition="top"
                >
                  <i class="pi pi-clock"></i> Pending
                </span>
              </ng-template>
            </ng-container>
            <ng-template #noParent>
              <span class="text-400">—</span>
            </ng-template>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center py-7">No locations found</td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>

  <p-card header="Services">
    <p-table
      [value]="onBoardingRecord?.locations || []"
      [paginator]="true"
      [rows]="5"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Location</th>
          <th>Provider</th>
          <th>Service Type</th>
          <th>Status</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-location>
        <tr>
          <td>{{ location.companyName }}</td>
          <td>{{ location.provider }}</td>
          <td>{{ location.serviceType }}</td>
          <td>
            <ng-container *ngIf="onBoardingRecord?.parentCustomerId; else noParentService">
              <span
                *ngIf="sentLocationNames.has(location.companyName); else pendingService"
                class="text-green-600"
                pTooltip="Created in Rev.io"
                tooltipPosition="top"
              >
                <i class="pi pi-check-circle"></i> Sent
              </span>
              <ng-template #pendingService>
                <span
                  class="text-orange-500"
                  pTooltip="Not yet sent to Rev.io"
                  tooltipPosition="top"
                >
                  <i class="pi pi-clock"></i> Pending
                </span>
              </ng-template>
            </ng-container>
            <ng-template #noParentService>
              <span class="text-400">—</span>
            </ng-template>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center py-7">No services found</td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>

  <p-card header="Items" *ngIf="matchedProducts.length > 0">
    <p-table
      [value]="matchedProducts"
      [paginator]="true"
      [rows]="10"
      [rowsPerPageOptions]="[10, 20, 50]"
      [globalFilterFields]="['site', 'product', 'description']"
    >
      <ng-template pTemplate="header">
        <tr>
          <th pSortableColumn="site">
            Site
            <p-sortIcon field="site"></p-sortIcon>
          </th>
          <th pSortableColumn="product">
            Product
            <p-sortIcon field="product"></p-sortIcon>
          </th>
          <th pSortableColumn="description">
            Description
            <p-sortIcon field="description"></p-sortIcon>
          </th>
          <th pSortableColumn="rate" class="text-right">
            Rate
            <p-sortIcon field="rate"></p-sortIcon>
          </th>
          <th pSortableColumn="quantity" class="text-right">
            Quantity
            <p-sortIcon field="quantity"></p-sortIcon>
          </th>
          <th pSortableColumn="effectiveDate">
            Effective Date
            <p-sortIcon field="effectiveDate"></p-sortIcon>
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-item>
        <tr>
          <td>{{ item.site }}</td>
          <td>{{ item.product }}</td>
          <td>{{ item.description }}</td>
          <td class="text-right">
            {{ item.rate !== undefined ? (item.rate | currency) : '-' }}
          </td>
          <td class="text-right">
            {{ item.quantity !== undefined ? item.quantity : '-' }}
          </td>
          <td>
            {{ item.effectiveDate ? item.effectiveDate : 'Today (default)' }}
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center py-7">No items found</td>
        </tr>
      </ng-template>
    </p-table>
  </p-card>
</div>

<p-confirmDialog [style]="{ width: '650px' }"></p-confirmDialog>

<div class="flex flex-column align-items-center gap-3 pt-4">

  <!-- Phase 1: send customers & sites -->
  <ng-container *ngIf="submissionPhase === 'none'">
    <span class="text-500 text-sm">Step 1 of 2: Create customers and sites in Rev.io</span>
    <span
      [pTooltip]="canAdd ? '' : 'You do not have permission to add'"
      [style.display]="'inline-block'"
    >
      <p-button
        label="Send Customers & Sites"
        icon="pi pi-building"
        iconPos="right"
        [loading]="loading"
        (onClick)="handleSendCustomersAndSites()"
        [disabled]="loading || matchedProducts.length === 0 || !isFormValid || !canAdd"
        severity="primary"
      ></p-button>
    </span>
  </ng-container>

  <!-- Phase 1b: missing locations after parent was created -->
  <ng-container *ngIf="submissionPhase === 'has-missing'">
    <div class="flex align-items-center gap-2 text-orange-500">
      <i class="pi pi-exclamation-triangle"></i>
      <span class="text-sm font-medium">
        {{ unsentLocations.length }} location(s) not yet sent to Rev.io
      </span>
    </div>
    <p-message
      severity="warn"
      [text]="'The following locations are missing: ' + unsentLocationNames"
      styleClass="w-full"
    ></p-message>
    <span
      [pTooltip]="canAdd ? '' : 'You do not have permission to add'"
      [style.display]="'inline-block'"
    >
      <p-button
        [label]="'Send ' + unsentLocations.length + ' missing location(s)'"
        icon="pi pi-send"
        iconPos="right"
        [loading]="loading"
        (onClick)="handleSendMissingLocations()"
        [disabled]="loading || !canAdd"
        severity="warning"
      ></p-button>
    </span>
  </ng-container>

  <!-- Phase 2: send items (after customers have been sent) -->
  <ng-container *ngIf="submissionPhase === 'customers-sent'">
    <div class="flex align-items-center gap-2 text-green-600">
      <i class="pi pi-check-circle"></i>
      <span class="text-sm font-medium">Customers &amp; sites created successfully in Rev.io</span>
    </div>
    <p-message
      severity="warn"
      text="Before sending items, make sure you have configured tax exemptions in Rev.io for this customer."
      styleClass="w-full"
    ></p-message>
    <span
      [pTooltip]="canAdd ? '' : 'You do not have permission to add'"
      [style.display]="'inline-block'"
    >
      <p-button
        label="Send Items"
        icon="pi pi-send"
        iconPos="right"
        [loading]="loading"
        (onClick)="handleSendItems()"
        [disabled]="loading || !canAdd"
        severity="success"
      ></p-button>
    </span>
  </ng-container>

  <!-- Completed -->
  <ng-container *ngIf="submissionPhase === 'completed'">
    <div class="flex align-items-center gap-2 text-green-600">
      <i class="pi pi-check-circle text-2xl"></i>
      <span class="font-semibold">Onboarding completed — all data sent to Rev.io</span>
    </div>
  </ng-container>

</div>
